import { useEffect, useRef, useState } from 'react'
import logger from 'logger'
import links from 'links'
import dayjs from 'date'
import { useIntl } from 'intl'
import { debounce } from 'throttle-debounce'
import type { ApplePayConfig, ApplePayInstance, ApplePayPaymentRequest } from '@recurly/recurly-js'
import { useApplePaySubmit, useInquire, usePaymentFlow, useRecurly } from 'modules/payment'

// we think that apple pay support is checked before the calling of this hook
const useApplePayButton = () => {
  const intl = useIntl()
  const { recurly } = useRecurly()
  const { submit, isProcessing, shippingForm, formRef } = usePaymentFlow()
  const { orderData, isFetching: isInquireFetching } = useInquire()

  const [ isReady, setReady ] = useState(false)
  const applePayRef = useRef<ApplePayInstance>(null)

  // collected deep-memo shipping form values to re-initialize apple pay
  const [ shippingFormValues, setShippingFormValues ] = useState<PaymentModule.ShippingFormFields>(() => shippingForm.getValues())

  const { submitApplePay } = useApplePaySubmit({ applePayRef, submit, payStep: 'Checkout' })

  useEffect(() => {
    let prevValues = shippingForm.getValues()

    // watch form changes with debouncing
    const forceUpdate = debounce(() => {
      const newValues = shippingForm.getValues()
      const isChanged = Object.keys(newValues).some((field) => {
        return newValues[field] !== prevValues[field]
      })

      if (isChanged) {
        prevValues = newValues
        setShippingFormValues(newValues)
        // reset readiness, because we need to re-initialise it
        setReady(false)
      }
    }, 1000)

    shippingForm.on('change', forceUpdate)

    return () => {
      shippingForm.off('change', forceUpdate)
    }
  }, [ shippingForm ])

  useEffect(() => {
    setReady(false)

    if (applePayRef.current) {
      applePayRef.current = null
    }

    if (!orderData) {
      return
    }

    try {
      const paymentRequest: ApplePayPaymentRequest = {
        total: {
          label: 'Total',
          amount: intl.formatPrice(orderData.total, { withCurrency: false }),
        },
      }

      // add subscription info with recurring payment
      if (orderData.hasSubscription) {
        const subscriptionItem = orderData.items.subscription

        paymentRequest.recurringPaymentRequest = {
          paymentDescription: subscriptionItem.label,
          regularBilling: {
            label: subscriptionItem.label,
            amount: intl.formatPrice(subscriptionItem.price, { withCurrency: false }),
            paymentTiming: 'recurring',
            recurringPaymentIntervalUnit: 'month',
            recurringPaymentIntervalCount: 1,
            // start now. the setting only affects the display in the apple widget
            recurringPaymentStartDate: dayjs().toDate(),
          },
          managementURL: `https://${window.location.host}${links.profile.subscriptionStatus}`,
        }
      }

      const config: ApplePayConfig = {
        country: shippingFormValues.country,
        currency: intl.currency,
        paymentRequest,
      }

      logger.info(config, '[ApplePay] config')

      const applePay = recurly.ApplePay({
        form: formRef.current,
        ...config,
      })

      applePay.ready(() => {
        logger.info(`[ApplePay] ready`)
        setReady(true)
      })

      applePayRef.current = applePay
    }
    catch (error) {
      logger.error(error, '[ApplePay] initialization failed')
    }
  }, [ intl, orderData, shippingFormValues, recurly, formRef ])

  return {
    submitApplePay,
    isReady: isReady && !isInquireFetching,
    isProcessing,
  }
}

export default useApplePayButton
